import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer, Dialog, Box, Typography, IconButton, Stack, Chip,
  CircularProgress, Alert, List, ListItem, ListItemText, Button,
  Tabs, Tab, ListItemIcon,
} from '@mui/material';
import {
  Close, SwapVert, Flag, ShoppingCart,
} from '@mui/icons-material';
import { v2 } from '../../../../services/api';
import StepChainView from './StepChainView';
import MaterialTallyPanel from './MaterialTallyPanel';
import ProgressIndicator from './ProgressIndicator';
import GenerationModeBadge from './GenerationModeBadge';
import { calcProgressForWO } from '../hooks/useProgressV2';
import { aggregateTally } from '../hooks/useTallyV2';
import { movementTypeColor, movementTypeLabel, signedQuantity } from '../utils/movementDisplay';

// Build a one-line human description for a combined-history row. The backend
// tags each row with a "kind": material movement, WO status change, or a
// linked customer-order status transition.
function describeHistoryRow(row) {
  if (row.kind === 'movement') {
    return `${movementTypeLabel(row.movement_type)} ${signedQuantity(row)} × ${row.item_code || ''}`.trim();
  }
  if (row.kind === 'status') {
    return `${row.from_status || '—'} → ${row.to_status}`;
  }
  if (row.kind === 'order_status') {
    const order = row.customer_order_number ? `Order ${row.customer_order_number}: ` : 'Order: ';
    return `${order}${row.from_status || '—'} → ${row.to_status}`;
  }
  return row.description || '';
}

function historyRowIcon(kind) {
  if (kind === 'status') return <Flag fontSize="small" />;
  if (kind === 'order_status') return <ShoppingCart fontSize="small" />;
  return <SwapVert fontSize="small" />;
}

function historyRowTimestamp(row) {
  const iso = row.created_at || row.changed_at || row.timestamp;
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function WOv2DetailDrawer({
  open,
  workOrderId,
  onClose,
  onCancelled,
  // 'drawer' (default) slides in from the right; 'dialog' opens a centered
  // popup - used by list pages where the drawer covered the table.
  variant = 'drawer',
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tabs: 0 = Details, 1 = History. History is lazy-loaded on first switch.
  const [tab, setTab] = useState(0);
  const [history, setHistory] = useState(null);      // null = not yet fetched
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Guards the lazy history fetch so it runs exactly once per opened WO. We use
  // a ref (not the `history`/`historyLoading` state) in the effect's condition:
  // if the effect depended on those, calling setHistoryLoading(true) would
  // change a dependency, re-run the effect, and its cleanup would cancel the
  // in-flight fetch before it resolved — leaving loading stuck forever.
  const historyFetchedForRef = useRef(null);

  useEffect(() => {
    if (!open || workOrderId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Reset per-WO state so a reused drawer instance doesn't show stale data
    // or a stale History fetch for the previous work order.
    setTab(0);
    setHistory(null);
    setHistoryError(null);
    historyFetchedForRef.current = null;
    v2.workOrders.retrieve(workOrderId)
      .then((res) => { if (!cancelled) setDetail(res.data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, workOrderId]);

  // Lazy-load the combined history feed the first time the History tab is
  // opened. Don't fetch when the drawer opens — only when the tab is viewed.
  useEffect(() => {
    if (tab !== 1 || workOrderId == null) return undefined;
    if (historyFetchedForRef.current === workOrderId) return undefined;
    historyFetchedForRef.current = workOrderId;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    v2.workOrders.history(workOrderId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        setHistory(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch((err) => {
        if (cancelled) return;
        setHistoryError(err);
        // Allow a retry on a later tab switch since this fetch failed.
        historyFetchedForRef.current = null;
      })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [tab, workOrderId]);

  const handleCancel = async () => {
    if (!detail) return;
    if (!window.confirm('Cancel this work order? Outstanding reservations will be released.')) return;
    try {
      await v2.workOrders.cancel(detail.id, { notes: 'Cancelled from detail drawer' });
      if (onCancelled) onCancelled(detail.id);
      // refresh
      const res = await v2.workOrders.retrieve(detail.id);
      setDetail(res.data);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  const progress = detail ? calcProgressForWO(detail) : null;
  const tally = detail ? aggregateTally(detail.recent_movements || []) : [];

  const body = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {detail ? detail.wo_number : 'Work Order'} <GenerationModeBadge sx={{ ml: 1 }} />
        </Typography>
        <IconButton onClick={onClose} aria-label="close" data-testid="drawer-close">
          <Close />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
      >
        <Tab label="Details" data-testid="drawer-tab-details" />
        <Tab label="History" data-testid="drawer-tab-history" />
      </Tabs>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load work order.</Alert>}
        {tab === 0 && detail && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Item</Typography>
              <Typography variant="body1">
                {detail.output_item_code} — {detail.output_item_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={`Status: ${detail.status}`} />
              <Chip size="small" label={`Qty: ${detail.quantity_to_produce}`} />
              {detail.due_date && <Chip size="small" label={`Due: ${detail.due_date}`} />}
              {detail.customer_order_number && (
                <Chip size="small" variant="outlined"
                  label={`Order ${detail.customer_order_number}${detail.line_number != null ? ` (line ${detail.line_number})` : ''}`} />
              )}
            </Box>

            <ProgressIndicator
              planned={progress?.planned}
              produced={progress?.produced}
              scrapped={progress?.scrapped}
              label="Production"
            />

            <StepChainView steps={detail.steps} />

            <Box data-testid="dependency-list">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Dependencies
              </Typography>
              {Array.isArray(detail.dependents) && detail.dependents.length > 0 ? (
                <List dense disablePadding>
                  {detail.dependents.map((d) => (
                    <ListItem key={d.id} disableGutters>
                      <ListItemText
                        primary={d.required_wo_number || `WO #${d.required_work_order}`}
                        secondary={`required by step #${d.requiring_step}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No sub-assembly dependencies.
                </Typography>
              )}
            </Box>

            <MaterialTallyPanel rows={tally} title="Material Tally (recent)" />

            <Box data-testid="status-log">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Status Log
              </Typography>
              {Array.isArray(detail.status_logs) && detail.status_logs.length > 0 ? (
                <List dense disablePadding>
                  {detail.status_logs.map((s) => (
                    <ListItem key={s.id} disableGutters>
                      <ListItemText
                        primary={`${s.from_status || '—'} → ${s.to_status}`}
                        secondary={`${s.changed_at}${s.changed_by_username ? ` by ${s.changed_by_username}` : ''}${s.notes ? ` — ${s.notes}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No status changes recorded.
                </Typography>
              )}
            </Box>

            {detail.status !== 'cancelled' && detail.status !== 'completed' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                <Button color="error" variant="outlined" onClick={handleCancel}>
                  Cancel Work Order
                </Button>
              </Box>
            )}
          </Stack>
        )}

        {tab === 1 && (
          <Box data-testid="history-tab">
            {historyLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress data-testid="history-loading" />
              </Box>
            )}
            {historyError && !historyLoading && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="history-error">
                Failed to load history.
              </Alert>
            )}
            {!historyLoading && !historyError && Array.isArray(history) && history.length === 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="history-empty">
                No history recorded for this work order.
              </Typography>
            )}
            {!historyLoading && !historyError && Array.isArray(history) && history.length > 0 && (
              <List dense disablePadding data-testid="history-list">
                {history.map((row, i) => (
                  <ListItem
                    key={row.id != null ? `${row.kind}-${row.id}` : `row-${i}`}
                    disableGutters
                    alignItems="flex-start"
                    data-testid={`history-item-${i}`}
                    data-kind={row.kind}
                  >
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                      {historyRowIcon(row.kind)}
                    </ListItemIcon>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {row.kind === 'movement' ? (
                            <Chip
                              size="small"
                              label={movementTypeLabel(row.movement_type)}
                              color={movementTypeColor(row.movement_type)}
                            />
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={row.kind === 'order_status' ? 'Order Status' : 'Status'}
                            />
                          )}
                          <Typography variant="body2">{describeHistoryRow(row)}</Typography>
                        </Box>
                      )}
                      secondary={`${historyRowTimestamp(row)}${
                        (row.created_by_username || row.changed_by_username)
                          ? ` · ${row.created_by_username || row.changed_by_username}`
                          : ''
                      }${row.notes ? ` — ${row.notes}` : ''}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  if (variant === 'dialog') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
        data-testid="wo-v2-detail-drawer"
      >
        {body}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      data-testid="wo-v2-detail-drawer"
    >
      {body}
    </Drawer>
  );
}
